import { Plan } from '../types'
import { getPaperAddon } from '../lib/helpers'
import { usePlans } from '../stateData/hooks'

interface PlanCategories {
  inkPlans: Plan[]
  tonerPlans: Plan[]
  yearlyPlans: Plan[]
  paypPlans: Plan[]
  inkPaperPlans: Plan[]
  defaultInkPlan?: Plan
  defaultTonerPlan?: Plan
  defaultPaypPlan?: Plan
  defaultYearlyPlan?: Plan
  hasPaperPlan: (plan: Plan) => boolean
  plansGroupedByProgram: Record<string, Plan[]>
}

interface UsePlanCategoriesProps {
  showInkAndPaperPlans?: boolean
  showTonerPlans?: boolean
}

export const usePlanCategories = ({
  showInkAndPaperPlans,
  showTonerPlans
}: UsePlanCategoriesProps): PlanCategories => {
  const plans = usePlans().data || []
  const hasPaperPlan = (plan: Plan) => getPaperAddon(plan.addons) !== undefined

  const inkPlans = plans.filter(
    (plan: Plan) =>
      plan.program?.includes('ink') &&
      plan.pages > 0 &&
      plan.program !== 'i_yearly_ink'
  )

  const tonerPlans = plans.filter((plan: Plan) =>
    plan.program?.includes('toner')
  )

  const yearlyPlans = plans.filter((plan: Plan) => plan.yearlyPlan)

  const paypPlans = plans
    .filter((plan: Plan) => plan.pages === 0)
    ?.sort((a, b) => (a.overageBlockSize ?? 0) - (b.overageBlockSize ?? 0))
    .map((plan: Plan) => ({
      ...plan,
      program: 'i_pay_as_you_print'
    }))

  const inkPaperPlans = showInkAndPaperPlans
    ? inkPlans
        .filter((plan: Plan) => hasPaperPlan(plan))
        .map((plan: Plan) => ({
          ...plan,
          program: 'i_ink_paper',
          price: getPaperAddon(plan.addons).priceWithPlan,
          paperFreeMonths: getPaperAddon(plan.addons).freeMonths,
          paperOverageBlockPrice: getPaperAddon(plan.addons).overageBlockPrice,
          inkOverageBlockPrice: plan.overageBlockPrice,
          overageBlockPrice: getPaperAddon(plan.addons)
            .overageBlockPriceWithPlan
        }))
    : []

  const defaultInkPlan = inkPlans.find((plan: Plan) => plan.isDefault)
  const defaultTonerPlan = tonerPlans.find((plan: Plan) => plan.isDefault)
  const defaultPaypPlan = paypPlans.find((plan: Plan) => plan.isDefault)
  const defaultYearlyPlan = yearlyPlans.find((plan: Plan) => plan.isDefault)

  const plansGroupedByProgram = {
    i_ink_paper: inkPaperPlans,
    i_ink: inkPlans,
    i_toner: showTonerPlans ? tonerPlans : [],
    i_yearly_ink: yearlyPlans,
    i_pay_as_you_print: paypPlans
  }

  return {
    inkPlans,
    tonerPlans,
    yearlyPlans,
    paypPlans,
    inkPaperPlans,
    defaultInkPlan,
    defaultTonerPlan,
    defaultPaypPlan,
    defaultYearlyPlan,
    plansGroupedByProgram,
    hasPaperPlan
  }
}
